import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const [checkingClaims, setCheckingClaims] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAdminClaim = async () => {
      if (loading) return;

      if (!currentUser) {
        if (!cancelled) {
          setIsAdmin(false);
          setCheckingClaims(false);
        }
        return;
      }

      try {
        // Force-refresh token so recently updated custom claims are reflected.
        const tokenResult = await currentUser.getIdTokenResult(true);
        if (!cancelled) {
          setIsAdmin(tokenResult?.claims?.admin === true);
        }
      } catch (error) {
        console.error('Failed to verify admin claim:', error);
        if (!cancelled) {
          setIsAdmin(false);
        }
      } finally {
        if (!cancelled) {
          setCheckingClaims(false);
        }
      }
    };

    checkAdminClaim();

    return () => {
      cancelled = true;
    };
  }, [currentUser, loading]);

  if (loading || checkingClaims) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#D63AB4] mx-auto mb-3" />
          <p className="text-sm text-stone-600">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-[#e7e5e4] bg-white p-6 text-center">
          <ShieldAlert className="h-8 w-8 text-[#D63AB4] mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-stone-900">Sign in required</h2>
          <p className="mt-2 text-sm text-stone-600">
            This area is restricted to authenticated administrators.
          </p>
          <Link
            to="/"
            className="mt-5 inline-flex items-center rounded-full bg-stone-900 px-4 py-2 text-sm font-semibold text-white"
          >
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center">
          <ShieldAlert className="h-8 w-8 text-red-600 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-stone-900">Admin access denied</h2>
          <p className="mt-2 text-sm text-stone-600">
            Your account does not have the required admin claim.
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Ask an existing admin to grant your account the Firebase custom claim `admin: true`.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
